import { LuckyDrawSettingsEntityService } from './services/lucky-draw-settings-entity.service';
import { LuckyDrawSettingsDataService } from './services/lucky-draw-settings-data.service';
import { tap, map, exhaustMap } from 'rxjs/operators';
import { Pagination } from '@core/models/pagination.model';
import { Observable, of, Subscription } from 'rxjs';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, FormControl } from '@angular/forms';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { LuckyDrawDialogComponent } from './dialogs/lucky-draw-edit.component';
import { LuckyDrawSettings } from '@core/models/lucky-draw-settings.model';
import { CurrencyHttpService } from '@core/services/currency-http.service';
import { AppPermissionService } from '@core/services/app-permission.service';

@Component({
  selector: 'kt-lucky-draw-settings',
  templateUrl: './lucky-draw-settings.component.html',
  styleUrls: ['./lucky-draw-settings.component.scss']
})
export class LuckyDrawSettingsComponent implements OnInit, OnDestroy {
  form: FormGroup;
  dropdown = {
    currencies: JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies')),
    perPage: this.dropdownHttpService.perPage
  };
  luckyDrawSettings$: Observable<LuckyDrawSettings[]>;

  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  subscription = new Subscription();
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  dataLength = 0;

  // permissions
  canCreateLuckyDrawSettings: boolean;
  canViewLuckyDrawSettings: boolean;

  private subscriptions = new Subscription();

  constructor(
    public dialog: MatDialog,
    private dropdownHttpService: DropdownHttpService,
    private loadingBar: LoadingBarService,
    private luckyDrawSettingsDataService: LuckyDrawSettingsDataService,
    private luckyDrawSettingsEntityService: LuckyDrawSettingsEntityService,
    private currencyHttpService: CurrencyHttpService,
    private appPermissionService: AppPermissionService,
  ) { }

  ngOnInit() {
    this.setCurrencyDropdown();
    this.formInit();
    this.reload();
    this.pagination = this.luckyDrawSettingsDataService.pagination;

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canCreateLuckyDrawSettings = appPermissions.create_lucky_draw_settings;
      this.canViewLuckyDrawSettings = appPermissions.view_lucky_draw_settings;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    this.loadingBar.start();
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    return this.luckyDrawSettings$ = this.luckyDrawSettingsEntityService.getWithQuery(`?page=${page}&perPage=${pageSize}${params}`).pipe(
      tap(res => {
        this.loading = false;
        this.dataLength = res.length;
        this.pagination = this.luckyDrawSettingsDataService.pagination;
        this.loadingBar.complete();
      })
    );
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onClear() {
    this.clearBtnLoading = true;
    this.formInit();
    this.onSubmit(true);
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = clearSearch ? false : true;
    this.loading = true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.loadingBar.start();
        return this.luckyDrawSettings$ = this.luckyDrawSettingsEntityService.getWithQuery(`?${this.params}&perPage=${this.pageSize}`).pipe(
          tap(res => {
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.dataLength = res.length;
            this.page = 1;
            this.pagination = this.luckyDrawSettingsDataService.pagination;
            this.loadingBar.complete();
          })
        );
      }),
    ).subscribe();
  }

  onOpenDialog(mode: string, luckyDrawSetting?: any) {
    if (mode === 'create') {
      this.openDialogBy(LuckyDrawDialogComponent, { mode: 'create' });
    } else if (mode === 'edit') {
      const setting = this.luckyDrawSettingsDataService.getById(luckyDrawSetting.id);
      this.subscription = setting.pipe(
        tap((res) => {
          if (res) {
            this.openDialogBy(LuckyDrawDialogComponent, { mode: 'edit', luckyDrawSetting: res });
          }
        })
      ).subscribe();
    }
  }

  private setCurrencyDropdown() {
    if (this.dropdown.currencies.length === 0) {
      // set interval to get currencies from sessionStorage, to prevent returning empty currency dropdown
      if (sessionStorage.getItem('currencies') != undefined && sessionStorage.getItem('currencies') != null && sessionStorage.getItem('currencies') !== '') {
        var interval = setInterval(() => {
          this.dropdown.currencies = JSON.parse(sessionStorage.getItem('currencies'));
          if (this.dropdown.currencies != null) {
            clearInterval(interval);
          }
        }, 100);
      } else {
        this.currencyHttpService.setCurrency().subscribe(res => {
          this.dropdown.currencies = res;
        });
      }
    }
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => (formData[key] !== '' && formData[key] !== null) ? fields[key] = formData[key] : key);
    return fields;
  }


  private openDialogBy(componentRef: any, data?: { mode?: any, luckyDrawSetting?: any }) {
    const dialogRef = this.dialog.open(componentRef, {
      width: '1200px',
      data: {
        mode: data.mode,
        luckyDrawSetting: data.luckyDrawSetting
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.onViewPageBy(this.page).subscribe();
      }
    });
  }

  private formInit() {
    this.form = new FormGroup({
      name: new FormControl(null),
      currency_id: new FormControl('all'),
    });
  }

  private reload() {
    this.onViewPageBy(this.page).subscribe();
  }

}
